import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface AccountPopoverProps {
  isOpen: boolean;
  onClose: () => void;
  isSidebarCollapsed: boolean;
  onLegalClick?: () => void;
  onSettingsClick?: () => void;
  onUpgradeClick?: () => void;
  onHistoriqueClick?: () => void;
  onAdminClick?: () => void;
  initialTab?: AccountTab;
}

type AccountTab = 'menu' | 'learn-more' | 'scoring' | 'transparency';

export default function AccountPopover({
  isOpen,
  onClose,
  isSidebarCollapsed,
  onLegalClick,
  onSettingsClick,
  onUpgradeClick,
  onHistoriqueClick,
  onAdminClick,
  initialTab = 'menu',
}: AccountPopoverProps) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const { signOut, user, features, organization, previewPlan, previewOnboarding } = useAuth();
  // Même règle que la sidebar : la feature du plan, ou un solde de crédits
  // achetés (un acheteur de bundle conserve ses analyses). Une découverte
  // offerte (code promo, pool bêta) voit l'entrée, grisée.
  const hasHistoryAccess = features.history || (organization?.creditBalance ?? 0) > 0;
  // Le Dashboard est un outil d'administration global (tous les clients).
  // On le réserve à la vraie vue super-admin : dès qu'un plan est simulé
  // (preview Free/Beta/Pro/Premium ou onboarding/signup), on le masque pour
  // refléter exactement ce que voit un client réel.
  const showAdmin = user?.isSuperAdmin === true && !previewPlan && !previewOnboarding;
  const [tab, setTab] = useState<AccountTab>(initialTab);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  // Sync tab with initialTab when popover opens, reset when it closes
  useEffect(() => {
    if (isOpen) setTab(initialTab);
    else setTab('menu');
  }, [isOpen, initialTab]);

  if (!isOpen) return null;

  // ── "En savoir plus" sub-view ──
  if (tab === 'learn-more') {
    return (
      <div
        ref={popoverRef}
        className={`fixed bottom-[73px] pastel:bottom-[104px] bg-white dark:bg-slate-800 shader:bg-white/10 shader:backdrop-blur-2xl border border-slate-200 dark:border-slate-700 shader:border-white/15 rounded-xl shadow-xl shader:shadow-black/30 w-[340px] z-[60] ${
          isSidebarCollapsed ? 'left-[6px] pastel:left-[37px]' : 'left-[16px] pastel:left-[47px]'
        }`}
      >
        {/* Header */}
        <div className="flex items-center gap-2 p-3 border-b border-slate-200 dark:border-slate-700 shader:border-white/10">
          <button
            onClick={() => setTab('menu')}
            className="p-1 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 shader:hover:bg-white/10 transition-colors"
          >
            <svg className="w-4 h-4 text-slate-500 dark:text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200">En savoir plus</h3>
        </div>

        {/* Links */}
        <div className="p-2 space-y-0.5">
          <button
            onClick={() => setTab('scoring')}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 shader:hover:bg-white/10 hover:text-slate-900 dark:hover:text-white"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <span>Comprendre le scoring</span>
          </button>

          <button
            onClick={() => setTab('transparency')}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 shader:hover:bg-white/10 hover:text-slate-900 dark:hover:text-white"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            <span>Transparence & donn&eacute;es</span>
          </button>
        </div>
      </div>
    );
  }

  // ── Scoring sub-view ──
  if (tab === 'scoring') {
    return (
      <div
        ref={popoverRef}
        className={`fixed bottom-[73px] pastel:bottom-[104px] bg-white dark:bg-slate-800 shader:bg-white/10 shader:backdrop-blur-2xl border border-slate-200 dark:border-slate-700 shader:border-white/15 rounded-xl shadow-xl shader:shadow-black/30 w-[340px] z-[60] ${
          isSidebarCollapsed ? 'left-[6px] pastel:left-[37px]' : 'left-[16px] pastel:left-[47px]'
        }`}
      >
        {/* Header */}
        <div className="flex items-center gap-2 p-3 border-b border-slate-200 dark:border-slate-700 shader:border-white/10">
          <button
            onClick={() => setTab('learn-more')}
            className="p-1 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 shader:hover:bg-white/10 transition-colors"
          >
            <svg className="w-4 h-4 text-slate-500 dark:text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200">Comprendre le scoring</h3>
        </div>

        {/* Content – replace back nav to go to learn-more */}
        <div className="p-4 space-y-3 max-h-[420px] overflow-y-auto">
          {/* Score zones */}
          <div className="space-y-2.5">
            {/* Green */}
            <div className="flex gap-3 items-start">
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-slate-800 dark:text-slate-200">80 - 100 : Conforme</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed">
                  Votre contenu ne présente pas de problème majeur. Vous pouvez directement consulter un avocat spécialisé pour une validation définitive.
                </p>
              </div>
            </div>

            {/* Yellow */}
            <div className="flex gap-3 items-start">
              <div className="w-2.5 h-2.5 rounded-full bg-yellow-500 mt-1.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-slate-800 dark:text-slate-200">60 - 79 : Vigilance</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed">
                  L'idée est solide mais certaines zones grises nécessitent des ajustements. Retravaillez les points signalés, puis consultez un avocat avant publication.
                </p>
              </div>
            </div>

            {/* Orange */}
            <div className="flex gap-3 items-start">
              <div className="w-2.5 h-2.5 rounded-full bg-orange-500 mt-1.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-slate-800 dark:text-slate-200">40 - 59 : Risque élevé</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed">
                  Des problèmes de conformité ont été identifiés. Le concept nécessite un retravail sérieux. Consulter un avocat à ce stade serait prématuré et coûteux.
                </p>
              </div>
            </div>

            {/* Red */}
            <div className="flex gap-3 items-start">
              <div className="w-2.5 h-2.5 rounded-full bg-red-500 mt-1.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-slate-800 dark:text-slate-200">0 - 39 : Non conforme</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed">
                  Infraction probable à la Loi Évin. Le concept doit être repris en profondeur. Pas besoin d'avocat à ce stade — révisez d'abord votre approche.
                </p>
              </div>
            </div>
          </div>

          {/* Separator */}
          <div className="border-t border-slate-200 dark:border-slate-700 shader:border-white/10 pt-3">
            <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
              Le score est calculé par{' '}
              <button
                onClick={() => setTab('transparency')}
                className="underline text-violet-600 dark:text-violet-400 hover:text-violet-800 dark:hover:text-violet-300 transition-colors"
              >
                deux experts IA indépendants
              </button>
              {' '}— l'un cherchant ce qui est conforme, l'autre ce qui ne l'est pas — puis arbitré par un troisième.
            </p>
          </div>

          {/* Disclaimer */}
          <div className="bg-violet-50 dark:bg-violet-900/20 shader:bg-violet-500/10 rounded-lg px-3 py-2.5">
            <p className="text-xs text-violet-700 dark:text-violet-300 shader:text-violet-200 leading-relaxed">
              Evinly est un outil d'aide à la décision, pas un avis juridique. Quel que soit votre score, seul un avocat spécialisé peut valider la conformité définitive de votre contenu.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Transparency sub-view ──
  if (tab === 'transparency') {
    return (
      <div
        ref={popoverRef}
        className={`fixed bottom-[73px] pastel:bottom-[104px] bg-white dark:bg-slate-800 shader:bg-white/10 shader:backdrop-blur-2xl border border-slate-200 dark:border-slate-700 shader:border-white/15 rounded-xl shadow-xl shader:shadow-black/30 w-[340px] z-[60] ${
          isSidebarCollapsed ? 'left-[6px] pastel:left-[37px]' : 'left-[16px] pastel:left-[47px]'
        }`}
      >
        {/* Header */}
        <div className="flex items-center gap-2 p-3 border-b border-slate-200 dark:border-slate-700 shader:border-white/10">
          <button
            onClick={() => setTab('learn-more')}
            className="p-1 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 shader:hover:bg-white/10 transition-colors"
          >
            <svg className="w-4 h-4 text-slate-500 dark:text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200">Transparence & données</h3>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4 max-h-[420px] overflow-y-auto">
          {/* Three-judge pipeline */}
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <svg className="w-4 h-4 text-violet-500 dark:text-white shader:text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              <p className="text-sm font-medium text-slate-800 dark:text-slate-200">Un pipeline à trois juges</p>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
              Chaque analyse passe par trois étapes&nbsp;: un premier modèle extrait les faits objectifs du contenu, deux juges argumentent ensuite en parallèle — l'un défend la conformité, l'autre cherche ce qui pourrait poser problème — puis un troisième modèle arbitre le verdict final en pondérant les arguments.
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed mt-1.5">
              Ce dispositif contradictoire est conçu pour éviter tout biais systématique vers l'interdiction. Principe de base&nbsp;: tout ce qui n'est pas interdit est autorisé.
            </p>
          </div>

          {/* AI souveraine */}
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <svg className="w-4 h-4 text-violet-500 dark:text-white shader:text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2L2 7l10 5 10-5-10-5z" /><path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" />
              </svg>
              <p className="text-sm font-medium text-slate-800 dark:text-slate-200">IA souveraine française</p>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
              Evinly utilise exclusivement Mistral, une intelligence artificielle française hébergée en Union européenne. Pas d'OpenAI, pas d'Anthropic, pas de DeepSeek, aucun sous-traitant extra-européen pour le traitement de vos contenus.
            </p>
          </div>

          {/* Stockage EU */}
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <svg className="w-4 h-4 text-violet-500 dark:text-white shader:text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
              </svg>
              <p className="text-sm font-medium text-slate-800 dark:text-slate-200">Données hébergées en Europe</p>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
              Vos images, textes et résultats d'analyse sont stockés sur des serveurs européens. La base de données et le stockage des fichiers sont hébergés sur notre propre serveur (PocketBase), en France.
            </p>
          </div>

          {/* Pas d'entrainement */}
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <svg className="w-4 h-4 text-violet-500 dark:text-white shader:text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              <p className="text-sm font-medium text-slate-800 dark:text-slate-200">Aucun entraînement sur vos données</p>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
              Vos contenus sont envoyés via l'API Mistral et ne sont jamais utilisés pour entraîner les modèles. Vos campagnes, visuels et concepts restent strictement confidentiels.
            </p>
          </div>

          {/* Source juridique */}
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <svg className="w-4 h-4 text-violet-500 dark:text-white shader:text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
              </svg>
              <p className="text-sm font-medium text-slate-800 dark:text-slate-200">Sources juridiques officielles</p>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
              Les textes de loi et la jurisprudence sont récupérés en temps réel depuis l'API officielle Légifrance (PISTE/DILA), sous Licence Ouverte 2.0. Seuls les textes publiés au Journal officiel font juridiquement foi.
            </p>
          </div>

          {/* Right to contest — RGPD Art. 22 */}
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <svg className="w-4 h-4 text-violet-500 dark:text-white shader:text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 12l2 2 4-4" />
                <circle cx="12" cy="12" r="10" />
              </svg>
              <p className="text-sm font-medium text-slate-800 dark:text-slate-200">Décision algorithmique & recours humain</p>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
              Les verdicts produits par Evinly sont le résultat d'un traitement automatisé. Conformément à l'article 22 du RGPD, vous disposez d'un droit à une intervention humaine si vous estimez qu'un verdict est erroné. Contactez-nous à <a href="mailto:privacy@blackthornsdesign.com" className="underline text-violet-600 dark:text-violet-400">privacy@blackthornsdesign.com</a> pour demander une révision.
            </p>
          </div>

          {/* RGPD summary */}
          <div className="bg-violet-50 dark:bg-violet-900/20 shader:bg-violet-500/10 rounded-lg px-3 py-2.5">
            <p className="text-xs text-violet-700 dark:text-violet-300 shader:text-violet-200 leading-relaxed">
              En résumé&nbsp;: IA française, serveurs européens, aucune exploitation de vos données, pipeline contradictoire à trois juges et recours humain garanti. Evinly est conçu pour respecter le RGPD et la confidentialité de vos projets.
              {onLegalClick && (
                <>
                  {' '}Consultez nos{' '}
                  <button
                    onClick={() => { onLegalClick(); onClose(); }}
                    className="underline text-violet-700 dark:text-violet-300 shader:text-violet-200 hover:text-violet-900 dark:hover:text-violet-100 transition-colors"
                  >
                    mentions légales
                  </button>.
                </>
              )}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Main menu ──
  return (
    <div
      ref={popoverRef}
      className={`absolute bottom-16 mb-2 bg-white dark:bg-slate-800 shader:bg-white/10 shader:backdrop-blur-2xl border border-slate-200 dark:border-slate-700 shader:border-white/15 rounded-xl shadow-xl shader:shadow-black/30 w-64 p-2 z-[60] fixed bottom-[73px] ${
        isSidebarCollapsed ? 'left-[6px] pastel:left-[37px]' : 'left-[16px] pastel:left-[47px]'
      }`}
    >
      {/* User email */}
      <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 shader:border-white/10">
        <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{user?.email}</p>
      </div>

      {/* Actions */}
      <div className="pt-2 space-y-0.5">
        {/* Historique — l'entrée reste VISIBLE pour tous (elle montre ce que
            débloque l'abonnement) mais devient inerte sans la feature. */}
        {onHistoriqueClick && (
          hasHistoryAccess ? (
            <button
              onClick={() => { onClose(); onHistoriqueClick(); }}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 shader:hover:bg-white/10 hover:text-slate-900 dark:hover:text-white"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 3v18h18M7 14l4-4 4 4 5-5" />
              </svg>
              <span>Historique</span>
            </button>
          ) : (
            <button
              type="button"
              disabled
              title="Historique — réservé aux abonnements"
              className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg text-slate-300 dark:text-slate-600 shader:text-white/25 cursor-not-allowed"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 3v18h18M7 14l4-4 4 4 5-5" />
              </svg>
              <span>Historique</span>
              <svg className="w-3.5 h-3.5 ml-auto" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12 1a5 5 0 0 0-5 5v3H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V11a2 2 0 0 0-2-2h-1V6a5 5 0 0 0-5-5zm0 2a3 3 0 0 1 3 3v3H9V6a3 3 0 0 1 3-3z" />
              </svg>
            </button>
          )
        )}

        {/* Dashboard — réservé à la vraie vue super-admin */}
        {showAdmin && onAdminClick && (
          <button
            onClick={() => { onClose(); onAdminClick(); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 shader:hover:bg-white/10 hover:text-slate-900 dark:hover:text-white"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <span>Dashboard</span>
          </button>
        )}

        {onHistoriqueClick && (
          <div className="my-1 border-t border-slate-200 dark:border-slate-700 shader:border-white/10" />
        )}

        {/* Forfaits & crédits — toujours visible : changer de forfait (Pro/Premium) + acheter des bundles/crédits */}
        {onUpgradeClick && (
          <button
            onClick={() => { onClose(); onUpgradeClick(); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors text-violet-600 dark:text-violet-400 shader:text-violet-300 hover:bg-violet-50 dark:hover:bg-violet-900/20 shader:hover:bg-violet-500/10"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <span>Forfaits & crédits</span>
          </button>
        )}

        {/* Settings */}
        {onSettingsClick && (
          <button
            onClick={() => { onClose(); onSettingsClick(); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 shader:hover:bg-white/10 hover:text-slate-900 dark:hover:text-white"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
            <span>Paramètres</span>
          </button>
        )}

        {/* Learn more */}
        <button
          onClick={() => setTab('learn-more')}
          className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 shader:hover:bg-white/10 hover:text-slate-900 dark:hover:text-white"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
          <span>En savoir plus</span>
          <svg className="w-3 h-3 ml-auto text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>

        {/* Support */}
        <a
          href="mailto:ludovic@blackthornsdesign.com"
          className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 shader:hover:bg-white/10 hover:text-slate-900 dark:hover:text-white"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          <span>Contacter le support</span>
        </a>

        {/* Logout */}
        <button
          onClick={() => { onClose(); signOut(); }}
          className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors text-red-500 dark:text-red-400 shader:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 shader:hover:bg-red-500/10"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
          <span>Se deconnecter</span>
        </button>
      </div>
    </div>
  );
}
